import { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Avatar, Chip, Divider, Grid,
  TextField, Button, IconButton, CircularProgress, Alert, Snackbar,
  MenuItem, Select, FormControl, InputLabel, Tabs, Tab, Badge,
  Tooltip, Dialog, DialogContent, DialogActions,
} from '@mui/material';
import EditOutlinedIcon          from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon          from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon        from '@mui/icons-material/CancelOutlined';
import PhotoCameraOutlinedIcon   from '@mui/icons-material/PhotoCameraOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon         from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon         from '@mui/icons-material/PersonOutline';
import SportsIcon                from '@mui/icons-material/Sports';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import BarChartOutlinedIcon      from '@mui/icons-material/BarChartOutlined';
import BusinessOutlinedIcon      from '@mui/icons-material/BusinessOutlined';
import axios from 'axios';
import { BASE_URL, apiCall } from '../dashboard/events/api/apiClient';

const CLOUDINARY_CLOUD_NAME    = 'ds1hlry5u';
const CLOUDINARY_UPLOAD_PRESET = 'tsyvx1hr';

const uploadToCloudinary = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  const res = await axios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, fd
  );
  return res.data.secure_url;
};

// ── Shared design tokens ───────────────────────────────────────────────────
const BG        = '#f5f8f6';
const SURFACE   = '#ffffff';
const BORDER    = '#d0ddd7';
const ACCENT    = '#2e7d5e';
const ACCENT_BG = '#e8f5ee';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';
// ──────────────────────────────────────────────────────────────────────────

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: '10px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      '& .MuiMenuItem-root': {
        color: TEXT_PRI, fontSize: 14,
        '&:hover': { bgcolor: ACCENT_BG },
        '&.Mui-selected': { bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 600 },
        '&.Mui-selected:hover': { bgcolor: ACCENT_BG },
      },
    },
  },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: SURFACE,
    color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
    '& input': { color: TEXT_PRI },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC, fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSelect-icon': { color: TEXT_SEC },
};

const POSITIONS     = ['GK','CB','RB','LB','RWB','LWB','SW','CDM','CM','CAM','RM','LM','RW','LW','CF','ST'];
const FEET          = ['left','right','both'];
const LICENCES      = ['A','B','C','D'];
const BLOOD_TYPES   = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];
const FITNESS_LEVELS = ['excellent','good','average','poor'];

// ── StatBar ────────────────────────────────────────────────────────────────
const StatBar = ({ label, value }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" mb={0.5}>
      <Typography variant="caption" sx={{ color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 700 }}>{value}</Typography>
    </Stack>
    <Box sx={{ height: 4, bgcolor: BORDER, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${value}%`, bgcolor: ACCENT, borderRadius: 2, transition: 'width .6s ease' }} />
    </Box>
  </Box>
);

// ── InfoRow ────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: TEXT_PRI, fontWeight: 600, mt: 0.3 }}>{value || '—'}</Typography>
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────
const Settings = () => {
  const [user, setUser]           = useState(null);
  const [entity, setEntity]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [editing, setEditing]     = useState(false);
  const [tab, setTab]             = useState(0);
  const [form, setForm]           = useState({});
  const [snack, setSnack]         = useState({ open: false, msg: '', severity: 'success' });
  const [uploadingPic, setUploadingPic]     = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoDialog, setPhotoDialog]       = useState(false);
  const profilePicRef = useRef();
  const photoRef      = useRef();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiCall((h) => axios.get(`${BASE_URL}/auth/me`, { headers: h }));
        setUser(data.data);
        setEntity(data.entity);
        initForm(data.data);
      } catch {
        setSnack({ open: true, msg: 'Impossible de charger le profil.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const initForm = (u) => setForm({
    firstName: u.firstName || '',
    lastName:  u.lastName  || '',
    licence:   u.licence   || '',
    profilePic: u.profilePic || '',
    photos:    u.photos    || [],
    birthday:  u.birthday  ? u.birthday.substring(0, 10) : '',
    birthCity: u.birthCity || '',
    nationality: u.nationality || '',
    phone:     u.phone     || '',
    positionOnField:    u.positionOnField    || '',
    actualJerseyNumber: u.actualJerseyNumber ?? '',
    preferredFoot:      u.preferredFoot      || '',
    medicalSituation: {
      bloodType:       u.medicalSituation?.bloodType       || '',
      height:          u.medicalSituation?.height          ?? '',
      weight:          u.medicalSituation?.weight          ?? '',
      knownConditions: u.medicalSituation?.knownConditions?.join(', ') || '',
      allergies:       u.medicalSituation?.allergies?.join(', ')       || '',
      fitnessLevel:    u.medicalSituation?.fitnessLevel    || '',
    },
  });

  const handleCancel = () => { initForm(user); setEditing(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        actualJerseyNumber: form.actualJerseyNumber !== '' ? Number(form.actualJerseyNumber) : undefined,
        medicalSituation: {
          ...form.medicalSituation,
          height: form.medicalSituation.height !== '' ? Number(form.medicalSituation.height) : undefined,
          weight: form.medicalSituation.weight !== '' ? Number(form.medicalSituation.weight) : undefined,
          knownConditions: form.medicalSituation.knownConditions
            ? form.medicalSituation.knownConditions.split(',').map(s => s.trim()).filter(Boolean) : [],
          allergies: form.medicalSituation.allergies
            ? form.medicalSituation.allergies.split(',').map(s => s.trim()).filter(Boolean) : [],
        },
      };
      const { data } = await apiCall((h) =>
        axios.put(`${BASE_URL}/users/${user._id}`, payload, { headers: h })
      );
      setUser(data.data || { ...user, ...payload });
      setEditing(false);
      setSnack({ open: true, msg: 'Profil mis à jour avec succès !', severity: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Erreur lors de la mise à jour.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPic(true);
    try {
      const url = await uploadToCloudinary(file);
      setForm(f => ({ ...f, profilePic: url }));
    } catch {
      setSnack({ open: true, msg: 'Erreur upload photo de profil.', severity: 'error' });
    } finally { setUploadingPic(false); }
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadToCloudinary(file);
      setForm(f => ({ ...f, photos: [...(f.photos || []), url] }));
    } catch {
      setSnack({ open: true, msg: 'Erreur upload photo.', severity: 'error' });
    } finally { setUploadingPhoto(false); }
  };

  const handleDeletePhoto = (idx) =>
    setForm(f => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const setField   = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setMedical = (key, val) => setForm(f => ({ ...f, medicalSituation: { ...f.medicalSituation, [key]: val } }));

  const displayName = user ? `${user.firstName} ${user.lastName}` : '';
  const initials    = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}` : '';
  const age         = user?.birthday
    ? Math.floor((Date.now() - new Date(user.birthday)) / (365.25 * 24 * 3600 * 1000))
    : null;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
      <CircularProgress sx={{ color: ACCENT }} />
    </Box>
  );

  return (
    <Box sx={{ color: TEXT_PRI }}>

      {/* ── Header ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={4} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ color: TEXT_PRI, fontWeight: 900, mb: 0.5 }}>Paramètres</Typography>
          <Typography variant="body2" sx={{ color: TEXT_SEC }}>Gérez votre compte et vos préférences</Typography>
        </Box>

        {!editing ? (
          <Button
            startIcon={<EditOutlinedIcon />}
            onClick={() => setEditing(true)}
            sx={{
              bgcolor: ACCENT_BG, color: ACCENT,
              border: `1px solid ${ACCENT}55`,
              borderRadius: '10px', fontWeight: 700, px: 3,
              textTransform: 'none',
              '&:hover': { bgcolor: '#d4eddf' },
            }}
          >
            Modifier
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<CancelOutlinedIcon />}
              onClick={handleCancel}
              sx={{
                color: TEXT_SEC, border: `1px solid ${BORDER}`,
                borderRadius: '10px', fontWeight: 600, px: 2,
                textTransform: 'none',
                '&:hover': { bgcolor: BG, borderColor: TEXT_SEC },
              }}
            >
              Annuler
            </Button>
            <Button
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveOutlinedIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: ACCENT, color: '#fff',
                borderRadius: '10px', fontWeight: 700, px: 3,
                textTransform: 'none',
                boxShadow: `0 4px 14px ${ACCENT}55`,
                '&:hover': { bgcolor: '#235f47', boxShadow: `0 6px 18px ${ACCENT}66` },
                '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
              }}
            >
              Enregistrer
            </Button>
          </Stack>
        )}
      </Stack>

      {/* ── Profile Hero Card ── */}
      <Paper
        elevation={0}
        sx={{
          p: 3, borderRadius: '16px',
          bgcolor: SURFACE, border: `1px solid ${BORDER}`,
          mb: 3, position: 'relative', overflow: 'hidden',
        }}
      >
        {/* decorative circle */}
        <Box sx={{
          position: 'absolute', top: 0, right: 0,
          width: 200, height: 200, borderRadius: '50%',
          bgcolor: ACCENT_BG, transform: 'translate(50%,-50%)',
          pointerEvents: 'none',
        }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={3}>
          {/* Avatar */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={editing && (
                <Tooltip title="Changer la photo">
                  <IconButton
                    size="small"
                    onClick={() => profilePicRef.current?.click()}
                    sx={{ bgcolor: ACCENT, color: '#fff', width: 28, height: 28, '&:hover': { bgcolor: '#235f47' } }}
                  >
                    {uploadingPic
                      ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                      : <PhotoCameraOutlinedIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Tooltip>
              )}
            >
              <Avatar
                src={editing ? form.profilePic : user?.profilePic}
                sx={{
                  width: 80, height: 80,
                  bgcolor: ACCENT_BG, color: ACCENT,
                  fontWeight: 800, fontSize: '1.8rem',
                  border: `2px solid ${BORDER}`,
                }}
              >
                {initials}
              </Avatar>
            </Badge>
            <input ref={profilePicRef} type="file" accept="image/*" hidden onChange={handleProfilePicUpload} />
          </Box>

          <Box flex={1}>
            <Typography variant="h5" sx={{ color: TEXT_PRI, fontWeight: 800 }}>{displayName}</Typography>
            <Typography variant="body2" sx={{ color: TEXT_SEC, mb: 1 }}>{user?.email}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              {/* role chip — green */}
              <Box sx={{ display:'inline-flex', alignItems:'center', px:1.25, py:0.3, borderRadius:'8px', border:`1px solid ${ACCENT}55`, bgcolor: ACCENT_BG, color: ACCENT, fontSize:12, fontWeight:700 }}>
                {user?.role}
              </Box>
              {user?.positionOnField && (
                <Box sx={{ display:'inline-flex', alignItems:'center', px:1.25, py:0.3, borderRadius:'8px', border:`1px solid ${BORDER}`, bgcolor: BG, color: TEXT_PRI, fontSize:12, fontWeight:600 }}>
                  {user.positionOnField}
                </Box>
              )}
              {user?.preferredFoot && (
                <Box sx={{ display:'inline-flex', alignItems:'center', px:1.25, py:0.3, borderRadius:'8px', border:`1px solid ${BORDER}`, bgcolor: BG, color: TEXT_SEC, fontSize:12 }}>
                  Pied {user.preferredFoot}
                </Box>
              )}
              {age && (
                <Box sx={{ display:'inline-flex', alignItems:'center', px:1.25, py:0.3, borderRadius:'8px', border:`1px solid ${BORDER}`, bgcolor: BG, color: TEXT_SEC, fontSize:12 }}>
                  {age} ans
                </Box>
              )}
            </Stack>
          </Box>

          {/* Entity badge */}
          {entity && (
            <Box sx={{ textAlign: { sm: 'right' } }}>
              <Typography variant="caption" sx={{ color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>
                Club / Académie
              </Typography>
              <Typography variant="body1" sx={{ color: TEXT_PRI, fontWeight: 700 }}>{entity.name}</Typography>
              <Typography variant="caption" sx={{ color: TEXT_SEC }}>{entity.city}, {entity.country}</Typography>
              <Box mt={0.5}>
                <Box sx={{ display:'inline-flex', px:1, py:0.2, borderRadius:'6px', border:`1px solid ${ACCENT}55`, bgcolor: ACCENT_BG, color: ACCENT, fontSize:11 }}>
                  {entity.type}
                </Box>
              </Box>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* ── Tabs ── */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': { color: TEXT_SEC, fontWeight: 600, textTransform: 'none', minWidth: 'auto', px: 2 },
          '& .Mui-selected': { color: ACCENT },
          '& .MuiTabs-indicator': { bgcolor: ACCENT },
        }}
      >
        <Tab icon={<PersonOutlineIcon fontSize="small" />} iconPosition="start" label="Infos personnelles" />
        <Tab icon={<SportsIcon fontSize="small" />} iconPosition="start" label="Profil sportif" />
        <Tab icon={<LocalHospitalOutlinedIcon fontSize="small" />} iconPosition="start" label="Médical" />
        <Tab icon={<BarChartOutlinedIcon fontSize="small" />} iconPosition="start" label="Statistiques" />
        <Tab icon={<BusinessOutlinedIcon fontSize="small" />} iconPosition="start" label="Entité" />
      </Tabs>

      {/* ── TAB 0 : Infos personnelles ── */}
      {tab === 0 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>
          <Typography variant="h6" sx={{ color: TEXT_PRI, fontWeight: 700, mb: 3 }}>Informations personnelles</Typography>
          <Grid container spacing={3}>
            {[
              { label: 'Prénom',             key: 'firstName' },
              { label: 'Nom',                key: 'lastName' },
              { label: 'Téléphone',          key: 'phone' },
              { label: 'Ville de naissance', key: 'birthCity' },
              { label: 'Nationalité',        key: 'nationality' },
            ].map(({ label, key }) => (
              <Grid item xs={12} sm={6} key={key}>
                {editing
                  ? <TextField fullWidth label={label} value={form[key]} onChange={e => setField(key, e.target.value)} sx={inputSx} />
                  : <InfoRow label={label} value={user?.[key]} />}
              </Grid>
            ))}

            <Grid item xs={12} sm={6}>
              {editing ? (
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Licence</InputLabel>
                  <Select value={form.licence} label="Licence" onChange={e => setField('licence', e.target.value)} MenuProps={menuProps}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {LICENCES.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : <InfoRow label="Licence" value={user?.licence} />}
            </Grid>

            <Grid item xs={12} sm={6}>
              {editing
                ? <TextField fullWidth label="Date de naissance" type="date" value={form.birthday}
                    onChange={e => setField('birthday', e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={inputSx} />
                : <InfoRow label="Date de naissance" value={user?.birthday ? new Date(user.birthday).toLocaleDateString('fr-FR') : '—'} />}
            </Grid>
          </Grid>

          {/* ── Photo gallery ── */}
          <Divider sx={{ my: 3, borderColor: BORDER }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ color: TEXT_PRI, fontWeight: 700 }}>Galerie photos</Typography>
            {editing && (
              <>
                <Button
                  startIcon={uploadingPhoto
                    ? <CircularProgress size={14} sx={{ color: ACCENT }} />
                    : <AddPhotoAlternateOutlinedIcon />}
                  onClick={() => photoRef.current?.click()}
                  disabled={uploadingPhoto}
                  sx={{
                    color: ACCENT, border: `1px solid ${ACCENT}55`,
                    borderRadius: '10px', fontWeight: 600, textTransform: 'none',
                    '&:hover': { bgcolor: ACCENT_BG },
                  }}
                >
                  Ajouter une photo
                </Button>
                <input ref={photoRef} type="file" accept="image/*" hidden onChange={handleAddPhoto} />
              </>
            )}
          </Stack>
          {(editing ? form.photos : user?.photos)?.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              {(editing ? form.photos : user?.photos).map((url, i) => (
                <Box key={i} sx={{ position: 'relative' }}>
                  <Box
                    component="img" src={url} alt={`photo-${i}`}
                    onClick={() => setPhotoDialog(url)}
                    sx={{
                      width: 90, height: 90, objectFit: 'cover',
                      borderRadius: '10px', border: `1px solid ${BORDER}`,
                      cursor: 'pointer', transition: 'transform .2s',
                      '&:hover': { transform: 'scale(1.04)' },
                    }}
                  />
                  {editing && (
                    <IconButton size="small" onClick={() => handleDeletePhoto(i)}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'rgba(220,38,38,.9)', color: '#fff', width: 20, height: 20, '&:hover': { bgcolor: '#b91c1c' } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucune photo</Typography>
          )}
        </Paper>
      )}

      {/* ── TAB 1 : Profil sportif ── */}
      {tab === 1 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>
          <Typography variant="h6" sx={{ color: TEXT_PRI, fontWeight: 700, mb: 3 }}>Profil sportif</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              {editing ? (
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Poste</InputLabel>
                  <Select value={form.positionOnField} label="Poste" onChange={e => setField('positionOnField', e.target.value)} MenuProps={menuProps}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {POSITIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : <InfoRow label="Poste" value={user?.positionOnField} />}
            </Grid>
            <Grid item xs={12} sm={6}>
              {editing
                ? <TextField fullWidth label="Numéro de maillot" type="number" value={form.actualJerseyNumber} onChange={e => setField('actualJerseyNumber', e.target.value)} sx={inputSx} />
                : <InfoRow label="Numéro de maillot" value={user?.actualJerseyNumber} />}
            </Grid>
            <Grid item xs={12} sm={6}>
              {editing ? (
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Pied préféré</InputLabel>
                  <Select value={form.preferredFoot} label="Pied préféré" onChange={e => setField('preferredFoot', e.target.value)} MenuProps={menuProps}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {FEET.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : <InfoRow label="Pied préféré" value={user?.preferredFoot} />}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── TAB 2 : Médical ── */}
      {tab === 2 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>
          <Typography variant="h6" sx={{ color: TEXT_PRI, fontWeight: 700, mb: 3 }}>Situation médicale</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              {editing ? (
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Groupe sanguin</InputLabel>
                  <Select value={form.medicalSituation.bloodType} label="Groupe sanguin" onChange={e => setMedical('bloodType', e.target.value)} MenuProps={menuProps}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {BLOOD_TYPES.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : <InfoRow label="Groupe sanguin" value={user?.medicalSituation?.bloodType} />}
            </Grid>
            <Grid item xs={12} sm={4}>
              {editing
                ? <TextField fullWidth label="Taille (cm)" type="number" value={form.medicalSituation.height} onChange={e => setMedical('height', e.target.value)} sx={inputSx} />
                : <InfoRow label="Taille" value={user?.medicalSituation?.height ? `${user.medicalSituation.height} cm` : undefined} />}
            </Grid>
            <Grid item xs={12} sm={4}>
              {editing
                ? <TextField fullWidth label="Poids (kg)" type="number" value={form.medicalSituation.weight} onChange={e => setMedical('weight', e.target.value)} sx={inputSx} />
                : <InfoRow label="Poids" value={user?.medicalSituation?.weight ? `${user.medicalSituation.weight} kg` : undefined} />}
            </Grid>
            <Grid item xs={12} sm={6}>
              {editing ? (
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Forme physique</InputLabel>
                  <Select value={form.medicalSituation.fitnessLevel} label="Forme physique" onChange={e => setMedical('fitnessLevel', e.target.value)} MenuProps={menuProps}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {FITNESS_LEVELS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : <InfoRow label="Forme physique" value={user?.medicalSituation?.fitnessLevel} />}
            </Grid>
            <Grid item xs={12} sm={6}>
              {editing
                ? <TextField fullWidth label="Conditions connues (séparées par virgule)" value={form.medicalSituation.knownConditions} onChange={e => setMedical('knownConditions', e.target.value)} sx={inputSx} />
                : (
                  <Box>
                    <Typography variant="caption" sx={{ color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>Conditions connues</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {user?.medicalSituation?.knownConditions?.length > 0
                        ? user.medicalSituation.knownConditions.map((c, i) => (
                            <Box key={i} sx={{ px:1, py:0.25, borderRadius:'6px', border:'1px solid #fed7aa', bgcolor:'#fff7ed', color:'#c2410c', fontSize:12 }}>{c}</Box>
                          ))
                        : <Typography variant="body2" sx={{ color: TEXT_SEC }}>—</Typography>}
                    </Stack>
                  </Box>
                )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {editing
                ? <TextField fullWidth label="Allergies (séparées par virgule)" value={form.medicalSituation.allergies} onChange={e => setMedical('allergies', e.target.value)} sx={inputSx} />
                : (
                  <Box>
                    <Typography variant="caption" sx={{ color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>Allergies</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {user?.medicalSituation?.allergies?.length > 0
                        ? user.medicalSituation.allergies.map((a, i) => (
                            <Box key={i} sx={{ px:1, py:0.25, borderRadius:'6px', border:'1px solid #fecaca', bgcolor:'#fef2f2', color:'#dc2626', fontSize:12 }}>{a}</Box>
                          ))
                        : <Typography variant="body2" sx={{ color: TEXT_SEC }}>—</Typography>}
                    </Stack>
                  </Box>
                )}
            </Grid>
          </Grid>

          {user?.medicalSituation?.injuries?.length > 0 && (
            <>
              <Divider sx={{ my: 3, borderColor: BORDER }} />
              <Typography variant="subtitle1" sx={{ color: TEXT_PRI, fontWeight: 700, mb: 2 }}>Historique des blessures</Typography>
              <Stack spacing={1.5}>
                {user.medicalSituation.injuries.map((inj, i) => (
                  <Paper key={i} elevation={0} sx={{ p: 1.5, borderRadius: '10px', bgcolor: BG, border: `1px solid ${BORDER}` }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: TEXT_PRI, fontWeight: 600 }}>{inj.type}</Typography>
                      <Box sx={{ px:1, py:0.2, borderRadius:'6px', fontSize:12, fontWeight:600,
                        bgcolor: inj.isActive ? '#fef2f2' : ACCENT_BG,
                        color:   inj.isActive ? '#dc2626' : ACCENT,
                        border:  `1px solid ${inj.isActive ? '#fecaca' : ACCENT + '55'}`,
                      }}>
                        {inj.isActive ? 'Active' : 'Récupéré'}
                      </Box>
                    </Stack>
                    {inj.description && <Typography variant="caption" sx={{ color: TEXT_SEC }}>{inj.description}</Typography>}
                  </Paper>
                ))}
              </Stack>
            </>
          )}
        </Paper>
      )}

      {/* ── TAB 3 : Statistiques ── */}
      {tab === 3 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>
          <Typography variant="h6" sx={{ color: TEXT_PRI, fontWeight: 700, mb: 3 }}>Statistiques du joueur</Typography>
          <Grid container spacing={4}>
            {[
              { label: 'Buts',       value: user?.playerStats?.goalsScored  ?? 0, color: ACCENT },
              { label: 'Passes D.',  value: user?.playerStats?.assists       ?? 0, color: '#2563eb' },
              { label: 'Matchs',     value: user?.playerStats?.matchesPlayed ?? 0, color: TEXT_PRI },
              { label: 'Minutes',    value: user?.playerStats?.minutesPlayed ?? 0, color: '#7c3aed' },
              { label: 'Cartons J.', value: user?.playerStats?.yellowCards   ?? 0, color: '#d97706' },
              { label: 'Cartons R.', value: user?.playerStats?.redCards      ?? 0, color: '#dc2626' },
            ].map(({ label, value, color }) => (
              <Grid item xs={6} sm={4} md={2} key={label}>
                <Box sx={{ textAlign: 'center', p: 2, borderRadius: '12px', bgcolor: BG, border: `1px solid ${BORDER}` }}>
                  <Typography variant="h4" sx={{ color, fontWeight: 900 }}>{value}</Typography>
                  <Typography variant="caption" sx={{ color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>{label}</Typography>
                </Box>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Divider sx={{ borderColor: BORDER, mb: 3 }} />
              <Typography variant="subtitle1" sx={{ color: TEXT_PRI, fontWeight: 700, mb: 2 }}>Attributs physiques & techniques</Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'Vitesse',         key: 'speed' },
                  { label: 'Endurance',        key: 'stamina' },
                  { label: 'Force',            key: 'strength' },
                  { label: 'Technique',        key: 'technicalRating' },
                  { label: 'Précision passe',  key: 'passAccuracy' },
                ].map(({ label, key }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <StatBar label={label} value={user?.playerStats?.[key] ?? 0} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── TAB 4 : Entité ── */}
      {tab === 4 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>
          <Typography variant="h6" sx={{ color: TEXT_PRI, fontWeight: 700, mb: 3 }}>Entité actuelle</Typography>
          {entity ? (
            <Grid container spacing={3}>
              {[
                { label: 'Nom',                  value: entity.name },
                { label: 'Type',                 value: entity.type },
                { label: 'Pays',                 value: entity.country },
                { label: 'Ville',                value: entity.city },
                { label: 'Fondée en',            value: entity.foundedYear },
                { label: 'Email de contact',     value: entity.contactInfo?.email },
                { label: 'Téléphone de contact', value: entity.contactInfo?.phone },
              ].map(({ label, value }) => (
                <Grid item xs={12} sm={6} key={label}><InfoRow label={label} value={value} /></Grid>
              ))}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>
                  Statut vérification
                </Typography>
                <Box mt={0.5}>
                  <Box sx={{
                    display: 'inline-flex', px: 1, py: 0.25, borderRadius: '6px', fontSize: 12, fontWeight: 600,
                    bgcolor: entity.isVerified ? ACCENT_BG : '#fef2f2',
                    color:   entity.isVerified ? ACCENT    : '#dc2626',
                    border: `1px solid ${entity.isVerified ? ACCENT + '55' : '#fecaca'}`,
                  }}>
                    {entity.isVerified ? 'Vérifiée' : 'Non vérifiée'}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucune entité associée.</Typography>
          )}
        </Paper>
      )}

      {/* ── Photo viewer dialog ── */}
      <Dialog open={!!photoDialog} onClose={() => setPhotoDialog(false)} maxWidth="md"
        PaperProps={{ sx: { borderRadius: '16px', bgcolor: SURFACE, border: `1px solid ${BORDER}` } }}>
        <DialogContent sx={{ p: 0 }}>
          {photoDialog && <Box component="img" src={photoDialog} sx={{ maxWidth: '90vw', maxHeight: '80vh', display: 'block' }} />}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${BORDER}`, px: 2, py: 1.5 }}>
          <Button onClick={() => setPhotoDialog(false)}
            sx={{ color: TEXT_SEC, borderRadius: '10px', textTransform: 'none', fontWeight: 600, border: `1px solid ${BORDER}`, '&:hover': { bgcolor: BG } }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{
            fontWeight: 700, borderRadius: '10px',
            ...(snack.severity === 'success' && {
              bgcolor: ACCENT_BG, color: ACCENT,
              border: `1px solid ${ACCENT}55`,
              '& .MuiAlert-icon': { color: ACCENT },
              '& .MuiIconButton-root': { color: ACCENT },
            }),
            ...(snack.severity === 'error' && {
              bgcolor: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca',
              '& .MuiAlert-icon': { color: '#dc2626' },
            }),
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;